import { addYears } from "date-fns";
import { FirebaseError } from "firebase/app";
import React, { useContext, useEffect, useState } from "react";
import { Settings } from "./ostertalertypes";
import { DBError, DB_ERROR, createSettings, fetchSettings } from "./utils/database";
import { dateToTimestamp } from "./utils/dateutils";

interface ISettingsContextProps {
  settings: Settings;
  setSettings: React.Dispatch<React.SetStateAction<Settings>>;
}

const defaultSettings: Settings = {
  reservation: {
    maxAllowedBookingDate: dateToTimestamp(addYears(new Date(), 1)),
    allocationCheckerPublic: false,
    emailOnReservationUpdate: false,
    bookingRestrictionDays: 14
  }
}

const defaultContextState = {
  settings: defaultSettings,
  setSettings: (settings: React.SetStateAction<Settings>) => {},
};

const SettingsContext = React.createContext<ISettingsContextProps>(defaultContextState);
// Define our own custom convenient hook
export const useSettingsContext = () => useContext(SettingsContext);

export const SettingsProvider: React.FC = (props) => {
  const [settings, setSettings] = useState<Settings>(defaultSettings);

  async function getSettings() {
    try {
      const settings = await fetchSettings();
      setSettings(settings);
    } catch (error) {
      if (error instanceof FirebaseError) {
        console.error("Failed fetching settings: " + error.message);
      }
      else if (error instanceof DBError && (error as DBError).errorCode === DB_ERROR.DOES_NOT_EXIST) {
        createSettings(defaultSettings);
        setSettings(defaultSettings);
        console.log("Created default settings.");
      } else {
        console.error("Failed fetching settings: " + JSON.stringify(error));
      }
    }
  }

  useEffect(() => {
    getSettings()
  }, []);

  const contextValue = {
    settings: settings,
    setSettings: setSettings
  };

  return <SettingsContext.Provider value={contextValue}>{props.children}</SettingsContext.Provider>;
};
